import {
  type FlexibleConnectedPositionStrategy,
  type Overlay,
  type OverlayRef,
} from '@angular/cdk/overlay';
import { type BehaviorSubject } from 'rxjs';

import { type DatasetRow } from '@blaze-shared/schema-domain';

import { type CellEditInfo, type ErrorFields } from './blaze-table.model';

export function extractRowsWithErrors(
  rows: (DatasetRow | undefined)[],
  errorFields: ErrorFields,
  columnName: string,
): {
  rowsToSave?: number[];
  rowsToUpdate?: { row: DatasetRow; rowNum: number }[];
} {
  const errorKeys = Object.keys(errorFields);
  if (!errorKeys) {
    return {};
  }

  const rowsToSave = errorKeys
    .filter((row) => row.startsWith('newRow'))
    .map((key) => parseInt(key.replace('newRow', '')));
  const rowsToUpdate = errorKeys
    .filter((row) => !row.startsWith('newRow'))
    .map((key) => {
      if (key && errorFields[key]?.[columnName]) {
        const rowNum = rows.findIndex((row) => row?._id === key);
        const row = rows[rowNum];
        if (!row) {
          return;
        }
        return { row, rowNum };
      }
      return;
    })
    .filter(Boolean) as { row: DatasetRow; rowNum: number }[];

  return { rowsToSave, rowsToUpdate };
}

export function destroyOverlayRef(
  overlayRef: OverlayRef,
  cellEditInfo: { cell: HTMLElement },
  cellEdit$$: BehaviorSubject<CellEditInfo | undefined>,
): void {
  overlayRef?.detach();
  overlayRef?.dispose();
  // clear cell after dispatch to make cell clickable again
  if (cellEdit$$.getValue()?.cell === cellEditInfo.cell) {
    cellEdit$$.next(undefined);
  }
}

export function generatePositionStrategyForOverlay(
  overlay: Overlay,
  relatedElement: HTMLElement,
  overlayPositionBelow?: boolean,
): FlexibleConnectedPositionStrategy | undefined {
  if (!relatedElement) {
    return;
  }

  return overlay
    .position()
    .flexibleConnectedTo(relatedElement)
    .withPositions(
      overlayPositionBelow
        ? [{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' }]
        : [{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' }],
    )
    .withFlexibleDimensions(true)
    .withGrowAfterOpen(true)
    .withPush(true);
}
